import { useEffect, useMemo } from 'react'
import { MeshLambertMaterial, MeshStandardMaterial } from 'three'

import {
  useCompressedGltf,
  useDecorationSpawner,
  useInstancedGeoFamily,
  useLevelGeometry,
  type TerrainMask,
} from '@hooks'
import { coordHash, toLambert, type ChunkConfig, type DecorationConfig } from '@utils'

const VARIANTS = [
  '/models/rocks/rock1-small_compressed.glb',
  '/models/rocks/rock2-small_compressed.glb',
  '/models/rocks/rock3-small_compressed.glb',
  '/models/rocks/rock4-small_compressed.glb',
  '/models/rocks/rock5-small_compressed.glb',
] as const
const NODES = [
  'Pebble_Round_5',
  'Pebble_Round_4',
  'Pebble_Round_3',
  'Pebble_Round_1',
  'Pebble_Round_2',
] as const
const S = 300

function getPebbleMaterial(mat: MeshStandardMaterial): MeshLambertMaterial {
  return toLambert(mat)
}

interface PebblesProps {
  mask: TerrainMask
  full?: boolean
}

export function Pebbles({ mask, full = true }: PebblesProps) {
  const { size } = useLevelGeometry()
  const frontZ = size.height / 2
  const gltfs = useCompressedGltf([...VARIANTS])

  const config = useMemo<DecorationConfig>(
    () => ({
      cellSize: 0.65,
      minTrackDist: 0.8,
      minSpacing: 0.35,
      density: 0.7,
      allow: (wx, wz, m) => (full || wz >= -frontZ) && m.sampleBrown(wx, wz),
      score: (wx, wz) => coordHash(wx, wz, S),
    }),
    [frontZ, full],
  )

  const chunkCfg = useMemo<ChunkConfig>(
    () => ({ side: mask.side, centerOffset: mask.centerOffset, chunksX: 4, chunksZ: 4 }),
    [mask],
  )

  const instances = useDecorationSpawner(mask, config, VARIANTS.length, S, 0.15, 0.8)

  const meshes = useInstancedGeoFamily(gltfs, NODES, instances, getPebbleMaterial, true, chunkCfg)

  useEffect(() => {
    return () => {
      meshes.forEach(mesh => mesh.geometry.dispose())
    }
  }, [meshes])

  return meshes.map((mesh, i) => <primitive key={i} object={mesh} />)
}
